import {
    Badge,
    Button,
    classNames,
    ContextMenuContext,
    Editable,
    FILockClosed,
    Icon,
    logger,
    ProgressCircle,
    ProgressPie,
    stopEvent,
    Text,
    View,
} from '@fold-dev/core'
import * as Token from '@fold-dev/design/tokens-es6'
import { useContext, useLayoutEffect, useMemo, useRef } from 'react'
import { TbChevronDown, TbChevronLeft, TbHash, TbLock, TbMessage, TbMessage2, TbMessage2Filled, TbMessageCircle, TbMessageDown } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { Block } from '../../components'
import { getInitials } from '../../helpers'
import { useProjects } from '../../hooks/api.hooks'
import { useAppStore } from '../../store/app'
import { BiHash } from 'react-icons/bi'
import { FiMessageCircle, FiMessageSquare } from 'react-icons/fi'

const Hash = ({ color = 'var(--f-color-text-weaker)' }) => {
    return (
        <svg
            width={21}
            height={21}
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                marginLeft: 10,
                marginRight: -4,
                borderRadius: 'var(--f-radius-md)',
                border: '1px solid var(--f-color-surface-strongest)',
            }}>
            {/* <rect width={21} height={21} rx={6} fill="var(--f-color-background)" /> */}
            <line
                x1={8}
                y1={4}
                x2={8}
                y2={17}
                stroke={color}
                strokeWidth={1.5}
            />
            <line
                x1={13}
                y1={4}
                x2={13}
                y2={17}
                stroke={color}
                strokeWidth={1.5}
            />
            <line
                x1={17}
                y1={8}
                x2={4}
                y2={8}
                stroke={color}
                strokeWidth={1.5}
            />
            <line
                x1={17}
                y1={13}
                x2={4}
                y2={13}
                stroke={color}
                strokeWidth={1.5}
            />
        </svg>
    )
}

export const Project = (props: any) => {
    const {
        id,
        name,
        color,
        icon,
        archived,
        isMain,
        unread,
        active,
        totalTaskCount,
        completeTaskCount,
        onNavigate,
        isNotURLChange,
        isChannel,
        isClosed,
        isPrivate,
        isChild,
        isParent,
        joinable,
        shortcode,
        projectUsers,
        onCollapse,
        collapsed,
    } = props
    const { workspaceId, projectId } = useParams()
    const navigate = useNavigate()
    const { setMenu } = useContext<any>(ContextMenuContext)
    const surfaceRef = useRef(null)
    const notifications = useAppStore((state) => state.notifications)
    const actions = useAppStore((state) => state.actions)
    const user = useAppStore((state) => state.user)
    const unreads = useAppStore((state) => state.unreads)
    const hasUnread = useMemo(() => !!unreads.find(({ projectId }) => projectId == id), [unreads])
    const projectsAPI = useProjects()
    const isActive = useMemo(() => active || projectId == id, [active, projectId, id])
    const projectName = useMemo(
        () => (isPrivate ? getInitials(projectUsers, user?.id) : name),
        [user?.id, isPrivate, projectUsers, name]
    )
    const projectImage = useMemo(
        () =>
            isPrivate
                ? projectUsers.length == 2
                    ? projectUsers.filter((u) => u.id != user?.id)![0].image
                    : null
                : null,
        [user?.id, isPrivate, projectUsers, name]
    )
    const projectUserId = useMemo(
        () =>
            isPrivate ? (projectUsers.length == 2 ? projectUsers.filter((u) => u.id != user?.id)![0].id : null) : null,
        [user?.id, isPrivate, projectUsers, name]
    )
    const showBadge = useMemo(
        () => (notifications[id] || []).some((n) => !n.read) || unread,
        [unread, projectId, notifications]
    )
    const className = classNames({
        'nav__project': true,
        'f-row': true,
        'is-child': isChild,
        'reveal-on-hover': true,
    })

    const handleNavigate = () => {
        if (isNotURLChange) {
            onNavigate()
        } else {
            if (id == projectId) return
            onNavigate()
        }
    }

    const handleContextMenuClick = (e) => {
        if (isMain) return
        stopEvent(e)
        setMenu(
            { id, name, color, icon, archived, isPrivate, isChannel, joinable, shortcode },
            { x: e.clientX, y: e.clientY }
        )
    }

    const handleChange = async (name) => {
        try {
            actions.projectUpdate({ id, name })
            await projectsAPI.update(workspaceId, projectId, { name })
        } catch (e) {
            logger(e)
        }
    }

    useLayoutEffect(() => {
        const el: any = surfaceRef.current
        el.addEventListener('contextmenu', handleContextMenuClick)
        return () => el.removeEventListener('contextmenu', handleContextMenuClick)
    })

    return (
        <div
            ref={surfaceRef}
            className={className}
            onClick={handleNavigate}>
            <View
                className={isActive ? 'nav__project-outer f-row is-active' : 'nav__project-outer f-row'}
                p={isMain ? '0rem 1rem 0rem 0.25rem' : '0rem 1rem 0rem 0rem'}>
                <div className="nav__project-icon f-row">
                    {isMain && (
                        <Block
                            isMain
                            isChannel={isChannel}
                            isPrivate={isPrivate}
                            otherUserId={projectUserId}
                            m="0 0 0 2px"
                            name={projectName}
                            src={projectImage}
                            icon={icon}
                            color={color}
                            size="md"
                        />
                    )}

                    {/* 
                    {!isMain && isPrivate && (
                        <Avatar
                            presence={id == 'c13304a9-34a6-46f7-9e89-a3d5934464b9' ? 'busy' : undefined}
                            name={privateProject.avatarName}
                            src={privateProject.avatarImage}
                            size="xs"
                        />
                    )}
                    */}

                    {!isMain && isPrivate &&  (
                        <Block
                            isPrivate={isPrivate}
                            isChannel={isChannel}
                            otherUserId={projectUserId}
                            name={projectName}
                            src={projectImage}
                            icon={icon}
                            color={isMain ? Token.ColorBase500 : color}
                            size="sm"
                        />
                    )}

                    {/* 
                    {!isMain && !isPrivate && (
                        <Icon
                            //icon={Bars3BottomLeftIcon}
                            icon={CheckCircleIcon}
                            color={color}  
                        />
                    )} 
                    
                    {!isMain && !isPrivate && (
                        <ProgressCircle
                            value={Math.floor(Math.random() * 100)}
                            width={19}
                            height={19}
                            size={15}
                            thickness={34}
                            style={{
                                '--f-progress-background': 'var(--f-color-surface-strongest)',
                                '--f-progress-active': color || 'var(--f-color-text-weakest)',
                            }}
                        />
                    )} 
                    */}
                       
                    {!isMain && !isPrivate && (
                        <ProgressPie
                            value={completeTaskCount / totalTaskCount * 100}
                            size={14}
                            padding={2}
                            radius={50}
                            style={{
                                '--f-progress-background': 'transparent',
                                '--f-progress-active': props.color || 'var(--f-color-text-weaker)',
                                'border': '2px solid ' + (props.color || 'var(--f-color-text-weaker)'),
                            }}
                        />
                    )}

                 {/*    {!isMain && !isPrivate && isChannel && (
                        <View
                            row
                            position="absolute"
                            color={color}
                            radius={50}
                            height={18}
                            width={18}
                            style={{ bottom: 5, left: 10 }}
                            zIndex={10} 
                            fontSize={13}>
                            <Icon 
                                style={{ '--f-icon-stroke-width-md': '3' }}
                                icon={TbMessageCircle}  
                            />
                        </View>
                    )} */}
                   
                </div>

                <div className="nav__project-text f-row f-ellipsis">
                    <Editable
                        selectOnFocus
                        useDoubleClick
                        disabled={isMain || isPrivate}
                        onChange={handleChange}
                        className="f-ellipsis">
                        <Text
                            fontWeight={isActive ? 'var(--f-font-weight-bold)' : 'var(--f-font-weight-medium)'}
                            className="project__text f-ellipsis"
                            color={
                                isActive && color
                                    ? color
                                    : isActive || hasUnread
                                    ? 'var(--f-color-project-text-active)'
                                    : 'var(--f-color-project-text-inactive)'
                            }>
                            {projectName}
                        </Text>
                    </Editable>
                </div>

                {isParent && (
                    <Button
                        width={21}
                        height={21}
                        border="none"
                        p="0"
                        radius="var(--f-radius-md)"
                        className={collapsed ? undefined : 'reveal-on-hover-target'}
                        onClick={(e) => {
                            stopEvent(e)
                            onCollapse()
                        }}
                        style={{
                            'margin': '0 -5px 0 10px',
                            '--f-button-background-color': 'transparent',
                            '--f-button-border-color': 'var(--f-color-surface-strong)',
                            '--f-button-background-color-hover': 'var(--f-color-surface)',
                        }}>
                        <Icon
                            icon={collapsed ? TbChevronLeft : TbChevronDown}
                            color="var(--f-color-text-weaker)"
                        />
                    </Button>
                )}

                {showBadge && (
                    <Badge
                        width={7}
                        height={7}
                        variant="accent"
                        bgToken="danger"
                        style={{ marginLeft: 15, marginRight: 3 }}
                    />
                )}

                {isChannel && !isPrivate && (
                    <Icon
                        size="sm"
                        icon={TbMessageCircle}
                        style={{ 
                            'marginLeft': 10, 
                            '--f-icon-sizing-sm': '15px',
                            //'--f-icon-stroke-width-sm': '3',
                        }}
                        color="var(--f-color-text-weaker)"
                        //color={color}
                    />
                )}

                {isClosed && !isPrivate && (
                    <Icon
                        size="sm"
                        icon={TbLock}
                        className="tabler-lock-icon"
                        style={{ marginLeft: 7 }}
                        color="var(--f-color-text-weaker)"
                    />
                )}

                {/* 
                {isChannel && !isPrivate && <Hash />}
 */}
                {/* 

                {showBadge && (
                    <Badge
                        bg={color}
                        //bg={Token.ColorAccent500}
                        size="sm"
                        style={{ marginLeft: 10 }}>
                        {Math.ceil(Math.random()*10)}
                    </Badge>
                )}

                {!isMain && (
                    <ProgressCircle
                        value={Math.floor(Math.random() * 100)}
                        size={15}
                        thickness={20}
                        style={{
                            '--f-progress-background': 'var(--f-color-base-600)',
                            '--f-progress-active': Token.ColorBase800 || color,
                            'marginLeft': 5,
                        }}
                    />
                )}      

                {!isMain && (
                    <Text 
                        size="sm"
                        fontWeight="bold"
                        colorToken="text-weakest">
                        12
                    </Text>
                )}

                {!isMain && (
                    <ProgressPie
                        value={Math.floor(Math.random() * 100)}
                        size={15}
                        thickness={25}
                        style={{
                            '--f-progress-background': 'var(--f-color-surface-strongest)',
                            '--f-progress-active':  Token.ColorBase400 || color,
                            'marginLeft': 5,
                        }}
                    />
                )} 

                <ProgressPie
                    value={Math.floor(Math.random() * 100)}
                    size={15}
                    thickness={25}
                    style={{
                        '--f-progress-background': 'var(--f-color-surface-stronger)',
                        '--f-progress-active': 'var(--f-color-text-weakest)',
                        'marginLeft': 5,
                    }}
                />

                <span style={{ opacity: 0.25 }}>{excerpt}</span> 
                {privateProjectAvatar.status ? <span style={{ opacity: 0.25 }}>{privateProjectAvatar.status}</span> : null}
                {isTyping ? <Spinner size="xs" color="var(--f-color-surface-strongest)" /> : null}
                {isBroadcast && <Icon icon={FIZap} style={{ opacity: 0.25, marginLeft: 10 }} />}
                */}
            </View>
        </div>
    )
}
